// «+ Nuevo vendedor»: alta orquestada en `retail-org` (Cognito + RBAC + padrón) con rol vendedor en la tienda
// y, si se captura, la wallet PCF donde se le depositan las comisiones.
import { useId, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { userMessageOf } from '@/api/errors';
import { Button, Input, PhoneInput, usePermissions } from '@/kit';
import {
    useLinkRetailUserWallet,
    useProvisionRetailUser,
} from '@/domains/retail/negocios/api/useRetailOrg';
import { WalletLinkField } from '@/domains/retail/negocios/components/WalletLinkField';
import {
    localMxPhone,
    WALLET_LINK_PERMISSION,
    type WalletFieldState,
} from '@/domains/retail/negocios/components/walletLink';
import {
    EditorDrawer,
    EditorFooter,
    FormSection,
    FormSelect,
} from '../../inventario/components/catalog/FormBits';
import { useRosterStores } from '../../lib/useRoster';
import {
    toRosterWriteError,
    useInvalidateRoster,
    useRosterAbilities,
} from '../../lib/rosterWrites';
import {
    EMPTY_SELLER_FORM,
    newSellerSchema,
    storeSelectOptions,
    toProvisionRequest,
    type NewSellerForm,
} from '../lib/newSellerForm';
import { useIntegrationLock } from '../../integration/components/IntegrationLock';

const UNVERIFIED_WALLET = 'Verifica la wallet o borra la CLABE para dar de alta sin wallet.';

function NewSellerEditor({ retailerId, onClose }: { retailerId: string; onClose: () => void }) {
    const formId = useId();
    const provision = useProvisionRetailUser(retailerId);
    const link = useLinkRetailUserWallet(retailerId);
    const invalidateRoster = useInvalidateRoster();
    const canLinkWallet = usePermissions().hasPermission(WALLET_LINK_PERMISSION);
    const stores = useRosterStores(retailerId);
    const options = useMemo(() => storeSelectOptions(stores.data ?? []), [stores.data]);
    const [error, setError] = useState<string | null>(null);
    const [wallet, setWallet] = useState<WalletFieldState>({ kind: 'empty' });
    const [walletError, setWalletError] = useState<string | null>(null);
    // Con el alta hecha, el siguiente envío solo vuelve a intentar vincular la wallet.
    const [createdUserId, setCreatedUserId] = useState<string | null>(null);
    const form = useForm<NewSellerForm>({
        resolver: zodResolver(newSellerSchema),
        defaultValues: EMPTY_SELLER_FORM,
    });
    const { errors, isSubmitting } = form.formState;
    // Mientras haya una petición en vuelo no se cambia nada ni se cierra el panel.
    const busy = isSubmitting || provision.isPending || link.isPending;
    const phone = useWatch({ control: form.control, name: 'phone' });

    const submit = async (values: NewSellerForm) => {
        setError(null);
        if (wallet.kind === 'unverified') {
            setWalletError(UNVERIFIED_WALLET);
            return;
        }
        let retailUserId = createdUserId;
        if (retailUserId === null) {
            const store = stores.data?.find((s) => s.storeId === values.storeId);
            try {
                const created = await provision.mutateAsync(toProvisionRequest(values, store));
                retailUserId = created.retailUserId;
            } catch (failure) {
                const mapped = toRosterWriteError(failure);
                if (mapped.field === 'email' || mapped.field === 'storeId') {
                    form.setError(mapped.field, { message: mapped.message });
                }
                setError(mapped.message);
                return;
            }
            setCreatedUserId(retailUserId);
            void invalidateRoster();
        }
        if (wallet.kind === 'verified') {
            try {
                await link.mutateAsync({ retailUserId, body: wallet.body });
            } catch (failure) {
                const message = `El vendedor quedó dado de alta, pero su wallet no se vinculó: ${userMessageOf(failure)}`;
                setError(message);
                // El aviso sobrevive aunque se cierre el panel.
                toast.error(message);
                return;
            }
        }
        const invitation = `Si es su primer acceso, le llega la invitación a ${values.email.trim()}.`;
        toast.success('Vendedor dado de alta', {
            description:
                wallet.kind === 'verified'
                    ? `${invitation} Su wallet quedó vinculada.`
                    : invitation,
        });
        onClose();
    };

    return (
        <EditorDrawer
            open
            onClose={() => {
                if (!busy) onClose();
            }}
            title="Nuevo vendedor"
            footer={
                <EditorFooter
                    formId={formId}
                    submitLabel={
                        createdUserId === null
                            ? 'Dar de alta'
                            : wallet.kind === 'empty'
                              ? 'Terminar sin wallet'
                              : 'Vincular wallet'
                    }
                    isPending={busy}
                    error={error}
                    onCancel={onClose}
                />
            }
        >
            <form
                id={formId}
                noValidate
                autoComplete="off"
                className="flex flex-col gap-5"
                onSubmit={(event) => {
                    void form.handleSubmit(submit)(event);
                }}
            >
                <fieldset disabled={createdUserId !== null || busy} className="contents">
                    <FormSection title="Datos">
                        <Input
                            label="Nombre"
                            required
                            placeholder="Ej. Juan Pérez"
                            error={errors.displayName?.message}
                            {...form.register('displayName')}
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <Input
                                label="Correo"
                                type="email"
                                required
                                placeholder="nombre@cadena.com"
                                hint="Le llega la invitación para crear su contraseña."
                                error={errors.email?.message}
                                {...form.register('email')}
                            />
                            <Controller
                                control={form.control}
                                name="phone"
                                render={({ field, fieldState }) => (
                                    <PhoneInput
                                        ref={field.ref}
                                        name={field.name}
                                        label="Teléfono"
                                        hint="Opcional."
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>
                    </FormSection>
                    <FormSection title="Tienda">
                        <Controller
                            control={form.control}
                            name="storeId"
                            render={({ field, fieldState }) => (
                                <FormSelect
                                    label="Tienda"
                                    required
                                    value={field.value}
                                    onChange={field.onChange}
                                    options={options}
                                    disabled={stores.isPending}
                                    placeholder={
                                        stores.isPending ? 'Cargando tiendas…' : 'Elige la tienda'
                                    }
                                    error={fieldState.error?.message}
                                />
                            )}
                        />
                        {stores.isError ? (
                            <p className="text-[11px] text-danger">
                                No se pudieron cargar las tiendas de tu cadena.
                            </p>
                        ) : null}
                        <p className="text-[10.5px] text-muted-foreground">
                            El acceso queda limitado a la tienda elegida.
                        </p>
                    </FormSection>
                </fieldset>
                {canLinkWallet ? (
                    <FormSection title="Wallet PCF">
                        <WalletLinkField
                            retailerId={retailerId}
                            hint="Opcional. Sin wallet, sus comisiones quedan pendientes."
                            suggestedPhone={localMxPhone(phone)}
                            error={walletError}
                            disabled={busy}
                            onStateChange={(next) => {
                                setWallet(next);
                                setWalletError(null);
                            }}
                        />
                    </FormSection>
                ) : null}
            </form>
        </EditorDrawer>
    );
}

/** El botón se oculta sin `retail.user.create`; sin cadena queda deshabilitado. */
export function NewSellerButton({ retailerId }: { retailerId: string | null }) {
    const { canCreateUser } = useRosterAbilities();
    const lock = useIntegrationLock();
    const [openCount, setOpenCount] = useState(0);
    const [open, setOpen] = useState(false);
    if (!canCreateUser) return null;
    return (
        <>
            <Button
                type="button"
                size="sm"
                disabled={retailerId === null || lock.locked}
                title={
                    retailerId === null
                        ? 'Necesitas una cadena asignada para dar de alta'
                        : lock.reason
                }
                className="h-6 px-2 text-[11px]"
                leftIcon={<Plus className="size-3" aria-hidden />}
                onClick={() => {
                    setOpenCount((count) => count + 1);
                    setOpen(true);
                }}
            >
                Nuevo vendedor
            </Button>
            {open && retailerId !== null ? (
                <NewSellerEditor
                    key={openCount}
                    retailerId={retailerId}
                    onClose={() => {
                        setOpen(false);
                    }}
                />
            ) : null}
        </>
    );
}
